/**
 * Soft G — optional ending record (L1.5). Pure: no DOM, no i18n import.
 *
 * Records ONLY what the user pressed:
 *  - cards actually shown at showdown per seat: entered cards, 「안 보여줌(머크)」
 *    or 「모름」
 *  - where the pot went: 「<seat> 팟 가져감」 / 「나눔」 / 「모름」
 *
 * Locks (scripts/test-soft-g-ending-record.mjs):
 *  - Subject is the seat name (BTN …) — never 이김/짐/won/lost, never 「나」/you.
 *  - No numbers (no ±bb / profit / deltas), no aggregation, no win/loss colour.
 *  - No fabrication: nothing is auto-filled (not even when everyone folds),
 *    no preselection, no default. Empty ending renders nothing at all.
 *  - Placement: one line right after the last process line (Copy / PNG / card /
 *    `#/h/`), meta-line size. Never in OG, title, headline, or Copy first line.
 * The process line stays the star. The ending is not a score.
 */
import { actorSeat, deriveHandFlow, flowPositions } from './handFlow'
import type { Card, EndingPot, EndingShown, HandEnding, HandState, Suit } from './types'

const SUIT_SYM: Record<Suit, string> = { s: '\u2660', h: '\u2665', d: '\u2666', c: '\u2663' }
const CARD_RE = /^[AKQJT98765432][shdc]$/

function isCard(v: unknown): v is Card {
  return typeof v === 'string' && CARD_RE.test(v)
}

function seatCode(pos: string, playerCount: number): string {
  if (playerCount === 2 && pos === 'BTN') return 'BTN/SB'
  return pos
}

function prettyCards(cards: [Card | null, Card | null]): string {
  return cards
    .filter((c): c is Card => Boolean(c))
    .map((c) => c[0]! + SUIT_SYM[c[1] as Suit])
    .join('')
}

/** Labels the ending line needs (a slice of UiStrings — caller passes `t()`). */
export type EndingLabels = {
  endingLineLabel: string
  endingShownMuck: (seat: string) => string
  endingShownUnknown: (seat: string) => string
  endingShownCards: (seat: string, cards: string) => string
  endingPotTo: (seat: string) => string
  endingPotSplit: string
  endingPotUnknown: string
}

/** Seats folded by a user-entered fold action. */
function foldedSeats(hand: HandState): Set<string> {
  const out = new Set<string>()
  for (const a of hand.actions) {
    if (a.type === 'fold') out.add(actorSeat(a.actor, hand))
  }
  return out
}

/**
 * Input: seats still in the hand (offered as equal-weight chips). Never used to
 * pick or fill anything — the list only bounds what the user may press.
 */
export function endingSeats(hand: HandState): string[] {
  return deriveHandFlow(hand).live
}

/** A seat may carry an ending mark when it is a table seat that did not fold. */
function seatAllowed(hand: HandState, seat: string, folded: Set<string>): boolean {
  return flowPositions(hand.playerCount).includes(seat) && !folded.has(seat)
}

/** True when the user pressed at least one ending value. */
export function endingHasValue(ending: HandEnding | undefined | null): boolean {
  if (!ending) return false
  if (ending.pot) return true
  return Object.values(ending.shown ?? {}).some((m) =>
    m === 'muck' || m === 'unknown' || (Array.isArray(m) && Boolean(m[0] || m[1])),
  )
}

/**
 * The single ending line (Copy / PNG / Share card / `#/h/`), or null.
 * Null when nothing was pressed, or when there is no process line to follow
 * (holes-only / empty records — the ending never stands in the meta position).
 */
export function formatEndingLine(hand: HandState, labels: EndingLabels): string | null {
  const ending = hand.ending
  if (!endingHasValue(ending) || hand.actions.length === 0) return null
  const folded = foldedSeats(hand)
  const parts: string[] = []
  for (const seat of flowPositions(hand.playerCount)) {
    const mark = ending!.shown?.[seat]
    if (!mark || !seatAllowed(hand, seat, folded)) continue
    const code = seatCode(seat, hand.playerCount)
    if (mark === 'muck') parts.push(labels.endingShownMuck(code))
    else if (mark === 'unknown') parts.push(labels.endingShownUnknown(code))
    else {
      const cards = prettyCards(mark)
      if (cards) parts.push(labels.endingShownCards(code, cards))
    }
  }
  const pot = ending!.pot
  if (pot?.kind === 'seat') {
    if (seatAllowed(hand, pot.seat, folded)) {
      parts.push(labels.endingPotTo(seatCode(pot.seat, hand.playerCount)))
    }
  } else if (pot?.kind === 'split') parts.push(labels.endingPotSplit)
  else if (pot?.kind === 'unknown') parts.push(labels.endingPotUnknown)
  if (!parts.length) return null
  return `${labels.endingLineLabel}: ${parts.join(' · ')}`
}

/** Restore an ending from storage / link (drops anything malformed). */
export function sanitizeEnding(raw: unknown, positions: string[]): HandEnding | undefined {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return undefined
  const o = raw as Record<string, unknown>
  const out: HandEnding = {}
  if (o.shown && typeof o.shown === 'object' && !Array.isArray(o.shown)) {
    const shown: NonNullable<HandEnding['shown']> = {}
    for (const [seat, m] of Object.entries(o.shown as Record<string, unknown>)) {
      if (!positions.includes(seat)) continue
      if (m === 'muck' || m === 'unknown') shown[seat] = m
      else if (Array.isArray(m)) {
        const pair: [Card | null, Card | null] = [isCard(m[0]) ? m[0] : null, isCard(m[1]) ? m[1] : null]
        if (pair[0] || pair[1]) shown[seat] = pair
      }
    }
    if (Object.keys(shown).length) out.shown = shown
  }
  const p = o.pot as Record<string, unknown> | undefined
  if (p && typeof p === 'object') {
    if (p.kind === 'split') out.pot = { kind: 'split' }
    else if (p.kind === 'unknown') out.pot = { kind: 'unknown' }
    else if (p.kind === 'seat' && typeof p.seat === 'string' && positions.includes(p.seat)) {
      out.pot = { kind: 'seat', seat: p.seat }
    }
  }
  return endingHasValue(out) ? out : undefined
}

// ---------------------------------------------------------------------------
// `#/h/` compact form — optional `e` field (absent when empty → no new bytes).
// `{ s?: { BB: ['Qs','Qd'] | 'm' | 'u' }, p?: 'BTN' | '=' | '?' }`
// ---------------------------------------------------------------------------
export type CompactEnding = {
  s?: Record<string, [string | null, string | null] | 'm' | 'u'>
  p?: string
}

export function compactEnding(ending: HandEnding | undefined): CompactEnding | undefined {
  if (!endingHasValue(ending)) return undefined
  const c: CompactEnding = {}
  const s: NonNullable<CompactEnding['s']> = {}
  for (const [seat, m] of Object.entries(ending!.shown ?? {})) {
    if (m === 'muck') s[seat] = 'm'
    else if (m === 'unknown') s[seat] = 'u'
    else if (Array.isArray(m) && (m[0] || m[1])) s[seat] = [m[0], m[1]]
  }
  if (Object.keys(s).length) c.s = s
  const pot: EndingPot | undefined = ending!.pot
  if (pot?.kind === 'seat') c.p = pot.seat
  else if (pot?.kind === 'split') c.p = '='
  else if (pot?.kind === 'unknown') c.p = '?'
  return c
}

/** Expand compact `e` into the raw shape `sanitizeEnding` accepts. */
export function expandEnding(c: unknown): unknown {
  if (!c || typeof c !== 'object' || Array.isArray(c)) return undefined
  const o = c as CompactEnding
  const shown: Record<string, EndingShown> = {}
  for (const [seat, m] of Object.entries(o.s ?? {})) {
    if (m === 'm') shown[seat] = 'muck'
    else if (m === 'u') shown[seat] = 'unknown'
    else if (Array.isArray(m)) shown[seat] = [m[0] as Card | null, m[1] as Card | null]
  }
  let pot: EndingPot | undefined
  if (o.p === '=') pot = { kind: 'split' }
  else if (o.p === '?') pot = { kind: 'unknown' }
  else if (typeof o.p === 'string' && o.p) pot = { kind: 'seat', seat: o.p }
  return { shown, pot }
}
